import {
  TellescopeModelFromName,
  useTellescopeMutation,
  useTellescopeQuery,
  useTellescopeQueryClient,
} from "@tellescope/tanstack-query";
import { Button, cn, ConfirmDialog, Input } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { formatName } from "@tellescope/utils";
import { useState, useMemo } from "react";
import { Search, Users } from "lucide-react";

export interface RelatedContactsSectionProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const RelatedContactsSection = ({ contact }: RelatedContactsSectionProps) => {
  const { t } = useTranslation("contacts");
  const queryClient = useTellescopeQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const relationshipIds = useMemo(() => {
    return contact.relationships?.map((rel) => rel.id) ?? [];
  }, [contact.relationships]);

  // Fetch all related contacts in a single batch query
  const relatedContactsQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: {
      ids: relationshipIds,
    },
    enabled: relationshipIds.length > 0,
  });

  const updateRelationshipsMutation = useTellescopeMutation({
    model: "endusers",
    method: "updateOne",
  });

  const handleRemoveRelatedContact = async () => {
    if (!selectedContactId) return;

    const latestContact = queryClient.getQueryData({
      model: "endusers",
      method: "getOne",
      request: contact.id,
    });

    const currentRelationships = latestContact?.relationships ?? contact.relationships ?? [];
    const updatedRelationships = currentRelationships.filter((rel) => rel.id !== selectedContactId);

    const relatedContact = queryClient.getQueryData({
      model: "endusers",
      method: "getOne",
      request: selectedContactId,
    });

    const relatedContactRelationships = relatedContact?.relationships ?? [];
    const updatedRelatedContactRelationships = relatedContactRelationships.filter((rel) => rel.id !== contact.id);

    try {
      // Relationships are bidirectional - must remove both sides
      // Remove A → B
      await updateRelationshipsMutation.mutateAsync({
        id: contact.id,
        relationships: updatedRelationships,
        _options: {
          replaceObjectFields: true,
        },
      });

      // Remove B → A
      await updateRelationshipsMutation.mutateAsync({
        id: selectedContactId,
        relationships: updatedRelatedContactRelationships,
        _options: {
          replaceObjectFields: true,
        },
      });

      setSelectedContactId(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Failed to remove relationship:", error);
    }
  };

  // Map contact data to relationships, preserving type information using reduce for efficiency
  const relatedContactsWithTypes = useMemo(() => {
    if (!relatedContactsQuery.data?.matches) return [];

    const contactsById = new Map(relatedContactsQuery.data.matches.map((c) => [c.id, c]));

    return (contact.relationships ?? []).reduce<
      { contact: TellescopeModelFromName<"endusers">; relationshipType: string }[]
    >((acc, relationship) => {
      const relatedContact = contactsById.get(relationship.id);
      if (relatedContact) {
        acc.push({
          contact: relatedContact,
          relationshipType: relationship.type ?? "Unknown",
        });
      }
      return acc;
    }, []);
  }, [relatedContactsQuery.data, contact.relationships]);

  const filteredRelatedContacts = useMemo(() => {
    if (!searchQuery) return relatedContactsWithTypes;
    const query = searchQuery.toLowerCase();
    return relatedContactsWithTypes.filter(
      ({ contact: c, relationshipType }) =>
        formatName(c).toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.phone?.includes(query) ||
        relationshipType.toLowerCase().includes(query)
    );
  }, [relatedContactsWithTypes, searchQuery]);

  const selectedRelatedContact = useMemo(() => {
    if (!selectedContactId) return null;
    return relatedContactsWithTypes.find(({ contact: c }) => c.id === selectedContactId) ?? null;
  }, [selectedContactId, relatedContactsWithTypes]);

  const isLoading = relatedContactsQuery.isLoading;

  if (isLoading && relationshipIds.length > 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading related contacts...")}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Search bar */}
      <div className="flex gap-4 items-center mb-4 p-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("Search related contacts...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Related Contacts List */}
      {filteredRelatedContacts.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center space-y-4">
            <Users className="mx-auto h-12 w-12 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              {searchQuery ? t("No related contacts found matching your search") : t("No related contacts found")}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-h-[300px] overflow-y-auto border rounded-md">
          <div className="space-y-1 p-2">
            {filteredRelatedContacts.map(({ contact: relatedContact, relationshipType }) => {
              const isSelected = selectedContactId === relatedContact.id;
              return (
                <button
                  key={relatedContact.id}
                  onClick={() => setSelectedContactId(isSelected ? null : relatedContact.id)}
                  className={cn(
                    "w-full text-left p-3 rounded-md transition-colors hover:bg-muted",
                    isSelected ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-muted/50"
                  )}
                  data-testid="related-contact-item"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{formatName(relatedContact)}</div>
                      {relatedContact.email && (
                        <div
                          className={cn("text-sm", isSelected ? "text-primary-foreground/70" : "text-muted-foreground")}
                        >
                          {relatedContact.email}
                        </div>
                      )}
                    </div>
                    <div className={cn("text-sm font-medium", isSelected ? "text-primary-foreground" : "text-primary")}>
                      {relationshipType}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Remove Button */}
      {selectedRelatedContact && (
        <div className="pt-2 p-1">
          <Button
            variant="outline"
            className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={() => setShowDeleteConfirmation(true)}
          >
            {t("Remove {{name}} from Related Contacts", { name: formatName(selectedRelatedContact.contact) })}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={showDeleteConfirmation}
        onOpenChange={(open) => !open && setShowDeleteConfirmation(false)}
        variant="destructive"
        title={t("Remove Related Contact")}
        description={t("Are you sure you want to remove this {{type}} relationship? This action cannot be undone.", {
          type: selectedRelatedContact?.relationshipType,
        })}
        confirmText={t("Remove")}
        onConfirm={handleRemoveRelatedContact}
      />
    </>
  );
};
